import urls from "@/constants/urls.js";
import { includedArrToMap } from "@/helpers/jsonapi";
import axios from "axios";
import { ref } from "vue";

function extractCatch(error) {
  const res = error && error.response;

  if (res && res.data.errors) {
    return res.data.errors;
  }

  if (res && res.data.error) {
    return res.data.error;
  }

  return error;
}

export function useDonorSubscriptions() {
  const channels = ref([]);
  const loading = ref(false);
  const errors = ref(null);

  async function fetchSubscriptions() {
    loading.value = true;
    try {
      const { data } = await axios.get(urls.donorSubscriptions);
      channels.value = data?.data?.channels || [];

      return channels.value;
    } catch (e) {
      console.error(e);
      errors.value = extractCatch(e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function unsubscribeCampaign(channel, campaignId) {
    return axios.post(urls.donorSubscriptionsCampaignUnsubscribe, {
      channel,
      campaign_id: Number(campaignId),
    });
  }

  function resubscribeCampaign(channel, campaignId) {
    return axios.post(urls.donorSubscriptionsCampaignResubscribe, {
      channel,
      campaign_id: Number(campaignId),
    });
  }

  function narrow(channel, unwantedCampaignIds) {
    return axios.post(urls.donorSubscriptionsNarrow, {
      channel,
      unwanted_campaign_ids: unwantedCampaignIds.map(Number),
    });
  }

  return {
    channels,
    loading,
    errors,
    fetchSubscriptions,
    unsubscribeCampaign,
    resubscribeCampaign,
    narrow,
  };
}

export function useDonorDonatedCampaigns() {
  const campaigns = ref([]);
  const loading = ref(false);
  const errors = ref(null);

  async function fetchCampaigns() {
    loading.value = true;
    try {
      const limit = 100;
      const maxPages = 10;
      const byId = new Map();

      for (let page = 1; page <= maxPages; page++) {
        const { data, headers } = await axios.get(
          `${urls.donorDonationsList}?extend=organization&page=${page}&limit=${limit}`
        );
        const rows = data?.data || [];
        const includedMap = includedArrToMap(data?.included || []);

        for (const donation of rows) {
          const campaignRef = donation.relationships?.campaign?.data;
          const orgRef = donation.relationships?.organization?.data;
          const id = Number(campaignRef?.id);

          if (!id || byId.has(id)) {
            continue;
          }

          const campaign = campaignRef && includedMap[`${campaignRef.type}_${campaignRef.id}`];
          const organization = orgRef && includedMap[`${orgRef.type}_${orgRef.id}`];

          byId.set(id, {
            id,
            name: donation.attributes?.campaign_name || campaign?.attributes?.title || "",
            orgName: organization?.attributes?.orgname || "",
          });
        }

        const total = Number(headers["x-total-donations"]) || 0;
        if (rows.length === 0 || page * limit >= total) {
          break;
        }
      }

      campaigns.value = Array.from(byId.values());

      return campaigns.value;
    } catch (e) {
      console.error(e);
      errors.value = extractCatch(e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    campaigns,
    loading,
    errors,
    fetchCampaigns,
  };
}
